import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
import {
  addShortcutClickListener,
  isAppShortcutsAvailable,
  type ShortcutClickListener,
} from "@src/appV2/NativeAdapters/appShortcuts.adapter";
import { useEffect, useRef } from "react";

import { handleShortcutClick, registerShortcuts } from "./manager";
import type { AppShortcutContext } from "./types";

interface UseAppShortcutsParams {
  isAuthorized: boolean;
}

export function useAppShortcuts({ isAuthorized }: UseAppShortcutsParams) {
  const contextRef = useRef<AppShortcutContext>({ isAuthorized });
  const isAvailable = isAppShortcutsAvailable();

  useEffect(() => {
    contextRef.current = { isAuthorized };
  }, [isAuthorized]);

  useEffect(() => {
    if (!isAvailable) {
      return;
    }

    const sync = async () => {
      await registerShortcuts(contextRef.current);
    };

    void sync();
  }, [isAuthorized, isAvailable]);

  useEffect(() => {
    let listenerHandle: ShortcutClickListener | undefined;

    if (isAvailable) {
      const setupListener = async () => {
        try {
          listenerHandle = await addShortcutClickListener(async (event) => {
            await handleShortcutClick(event.shortcutId, contextRef.current);
          });
        } catch (error) {
          logError(APP_V2_APP_EVENTS.APP_SHORTCUT_LISTENER_FAILURE, { error });
        }
      };

      void setupListener();
    }

    return () => {
      void listenerHandle?.remove();
    };
  }, [isAvailable]);
}
